import React, { useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { apiFetch } from "./utils";
import { BRAND, KofidesLogo } from "./ui";

async function apiGet(path) {
  const res = await apiFetch(path);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Error de red (" + res.status + ")");
  }
  return res.json();
}

// --- Helpers de bucket de atraso (mismo criterio que VencBadge: 0-30 verde, 31-60 ambar, +60 rojo) ---
function bucketOf(diasAtraso) {
  if (diasAtraso <= 30) return "verde";
  if (diasAtraso <= 60) return "ambar";
  return "rojo";
}
const BUCKET_COLOR = { verde: "#2f855a", ambar: "#c05621", rojo: "#c53030" };
const BUCKET_BG = { verde: "#eafaf1", ambar: "#fff4e5", rojo: "#fde8e8" };
const BUCKET_LABEL = { verde: "Al día (0-30d)", ambar: "Atrasado (31-60d)", rojo: "Crítico (+60d)" };

function fmtMoney(n) {
  return (n || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function AtrasoBadge({ dias }) {
  const b = bucketOf(dias);
  return (
    <span
      style={{
        background: BUCKET_BG[b],
        color: BUCKET_COLOR[b],
        borderRadius: 6,
        padding: "2px 8px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {dias}d
    </span>
  );
}

function FaltaRepBadge() {
  return (
    <span
      style={{
        background: "#fff4e5",
        color: "#c05621",
        borderRadius: 6,
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 600,
        marginLeft: 6,
      }}
      title="Cobrado por banco pero sin REP (complemento de pago) emitido"
    >
      Falta REP
    </span>
  );
}

// --- Donut simple en SVG, sin dependencias externas ---
function AgingDonut({ totales }) {
  const total = totales.verde + totales.ambar + totales.rojo;
  if (total <= 0) return null;
  const order = ["verde", "ambar", "rojo"];
  const R = 70, C = 90, STROKE = 26;
  const circumference = 2 * Math.PI * R;
  let offsetAcc = 0;
  const arcs = order.map((k) => {
    const frac = totales[k] / total;
    const dash = frac * circumference;
    const el = (
      <circle
        key={k}
        cx={C}
        cy={C}
        r={R}
        fill="none"
        stroke={BUCKET_COLOR[k]}
        strokeWidth={STROKE}
        strokeDasharray={`${dash} ${circumference - dash}`}
        strokeDashoffset={-offsetAcc}
        transform={`rotate(-90 ${C} ${C})`}
      />
    );
    offsetAcc += dash;
    return el;
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
      <svg width={C * 2} height={C * 2}>
        {arcs}
        <text x={C} y={C - 6} textAnchor="middle" fontSize="15" fontWeight="700">
          {fmtMoney(total)}
        </text>
        <text x={C} y={C + 14} textAnchor="middle" fontSize="11" fill="#718096">
          Total
        </text>
      </svg>
      <div>
        {order.map((k) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: BUCKET_COLOR[k] }} />
            <span style={{ fontSize: 13 }}>
              {BUCKET_LABEL[k]} — {fmtMoney(totales[k])}{" "}
              <span style={{ color: "#a0aec0" }}>
                ({total > 0 ? Math.round((totales[k] / total) * 100) : 0}%)
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <div
      style={{
        flex: 1,
        background: "#fff",
        border: "1px solid #edf2f7",
        borderRadius: 10,
        padding: "16px 18px",
        minWidth: 160,
      }}
    >
      <div style={{ fontSize: 12, color: "#718096", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || "#1a202c" }}>{value}</div>
    </div>
  );
}

function ClienteRow({ cli }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        style={{ cursor: "pointer", borderBottom: "1px solid #edf2f7" }}
        onClick={() => setOpen((o) => !o)}
      >
        <td style={{ padding: "10px 12px" }}>{open ? "▾" : "▸"} {cli.nombre || "(sin nombre)"}</td>
        <td style={{ padding: "10px 12px", color: "#718096" }}>{cli.rfc || "—"}</td>
        <td style={{ padding: "10px 12px" }}>{cli.facturas.length}</td>
        <td style={{ padding: "10px 12px" }}><AtrasoBadge dias={cli.mayorAtraso} /></td>
        <td style={{ padding: "10px 12px", fontWeight: 700, textAlign: "right" }}>
          {fmtMoney(cli.totalAdeudado)}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} style={{ padding: 0, background: "#f9fafb" }}>
            <table style={{ width: "100%", fontSize: 13 }}>
              <thead>
                <tr style={{ color: "#718096", textAlign: "left" }}>
                  <th style={{ padding: "6px 24px" }}>Folio</th>
                  <th style={{ padding: "6px 12px" }}>Fecha</th>
                  <th style={{ padding: "6px 12px" }}>Método</th>
                  <th style={{ padding: "6px 12px", textAlign: "right" }}>Total</th>
                  <th style={{ padding: "6px 12px", textAlign: "right" }}>Cubierto</th>
                  <th style={{ padding: "6px 12px", textAlign: "right" }}>Pendiente</th>
                  <th style={{ padding: "6px 12px" }}>Atraso</th>
                </tr>
              </thead>
              <tbody>
                {cli.facturas.map((f) => (
                  <tr key={f.uuid} style={{ borderTop: "1px solid #edf2f7" }}>
                    <td style={{ padding: "6px 24px" }}>{f.folio || "—"}</td>
                    <td style={{ padding: "6px 12px" }}>{f.fecha}</td>
                    <td style={{ padding: "6px 12px" }}>{f.metodoPago}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right" }}>{fmtMoney(f.total)}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right" }}>{fmtMoney(f.cubierto)}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: 600 }}>
                      {fmtMoney(f.pendiente)}
                    </td>
                    <td style={{ padding: "6px 12px" }}>
                      <AtrasoBadge dias={f.diasAtraso} />
                      {f.faltaRep && <FaltaRepBadge />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

export function CuentasPorCobrarModule(p) {
  const uid = p.user.id;
  const [tab, setTab] = useState("pendientes"); // "pendientes" | "corte"
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";

  // Filtro principal: dispara la recarga de clientes/facturas (KPIs, donut, tabla).
  const [hastaLista, setHastaLista] = useState(today);
  const [fechaCorte, setFechaCorte] = useState(today);

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState("");

  // Filtro independiente del panel "Resumen de Cobranza": solo dispara su propio fetch.
  const [desdeResumen, setDesdeResumen] = useState(firstOfMonth);
  const [hastaResumen, setHastaResumen] = useState(today);
  const [resumen, setResumen] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [errorResumen, setErrorResumen] = useState(null);

  const loadLista = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === "pendientes") {
        const data = await apiGet(`/cuentas-cobrar/pendientes/${uid}?hasta=${hastaLista}`);
        setClientes(data.clientes || []);
      } else {
        const data = await apiGet(`/cuentas-cobrar/corte/${uid}?fecha=${fechaCorte}`);
        setClientes(data.clientes || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab, uid, hastaLista, fechaCorte]);

  useEffect(() => {
    loadLista();
  }, [loadLista]);

  const loadResumen = useCallback(async () => {
    if (tab !== "pendientes") {
      setResumen(null);
      return;
    }
    setLoadingResumen(true);
    setErrorResumen(null);
    try {
      const data = await apiGet(`/cuentas-cobrar/resumen/${uid}?desde=${desdeResumen}&hasta=${hastaResumen}`);
      setResumen(data);
    } catch (e) {
      setErrorResumen(e.message);
    } finally {
      setLoadingResumen(false);
    }
  }, [tab, uid, desdeResumen, hastaResumen]);

  useEffect(() => {
    loadResumen();
  }, [loadResumen]);

  // Totales por bucket para KPIs + donut (suma de PENDIENTE, no de total)
  const totales = { verde: 0, ambar: 0, rojo: 0 };
  let saldoTotal = 0;
  clientes.forEach((cli) => {
    cli.facturas.forEach((f) => {
      totales[bucketOf(f.diasAtraso)] += f.pendiente;
      saldoTotal += f.pendiente;
    });
  });

  const clientesFiltrados = clientes.filter((cli) => {
    if (!busqueda.trim()) return true;
    const s = busqueda.toLowerCase();
    return (cli.nombre || "").toLowerCase().includes(s) || (cli.rfc || "").toLowerCase().includes(s);
  });

  function exportarExcel() {
    const rows = [];
    clientes.forEach((cli) => {
      cli.facturas.forEach((f) => {
        rows.push({
          Cliente: cli.nombre,
          RFC: cli.rfc,
          Folio: f.folio,
          UUID: f.uuid,
          Fecha: f.fecha,
          MetodoPago: f.metodoPago,
          Total: f.total,
          CubiertoREP: f.cubiertoRep,
          CubiertoBanco: f.cubiertoBanco,
          Pendiente: f.pendiente,
          DiasAtraso: f.diasAtraso,
          FaltaREP: f.faltaRep ? "Sí" : "",
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CuentasPorCobrar");
    XLSX.writeFile(wb, `cuentas_por_cobrar_${tab === "pendientes" ? "hasta_" + hastaLista : fechaCorte}.xlsx`);
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9" }}>
      <div style={{ background: BRAND, padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <KofidesLogo size={28} />
          <p style={{ color: "#fff", fontWeight: 800, fontSize: 15, margin: 0, fontFamily: "Georgia,serif", letterSpacing: 2 }}>KOFIDES</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={p.onBack} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, padding: "7px 13px", color: "#fff", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Inicio</button>
          <button onClick={p.onLogout} style={{ background: "rgba(255,100,100,0.3)", border: "none", borderRadius: 8, padding: "7px 13px", color: "#fff", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Salir</button>
        </div>
      </div>
    <div style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 4 }}>Cuentas por Cobrar</h2>

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setTab("pendientes")}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            background: tab === "pendientes" ? "#2b6cb0" : "#fff",
            color: tab === "pendientes" ? "#fff" : "#2d3748",
            cursor: "pointer",
          }}
        >
          Saldo Pendiente
        </button>
        <button
          onClick={() => setTab("corte")}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            background: tab === "corte" ? "#2b6cb0" : "#fff",
            color: tab === "corte" ? "#fff" : "#2d3748",
            cursor: "pointer",
          }}
        >
          Corte de cartera
        </button>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        {tab === "pendientes" ? (
          <label style={{ fontSize: 13 }}>
            Mostrar pendientes emitidos hasta:{" "}
            <input type="date" value={hastaLista} onChange={(e) => setHastaLista(e.target.value)} />
          </label>
        ) : (
          <label style={{ fontSize: 13 }}>
            Fecha de corte{" "}
            <input type="date" value={fechaCorte} onChange={(e) => setFechaCorte(e.target.value)} />
          </label>
        )}
        <input
          placeholder="Buscar cliente o RFC..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0", minWidth: 220 }}
        />
        <button
          onClick={exportarExcel}
          disabled={!clientes.length}
          style={{
            marginLeft: "auto",
            padding: "8px 14px",
            borderRadius: 8,
            border: "none",
            background: "#2b6cb0",
            color: "#fff",
            cursor: clientes.length ? "pointer" : "not-allowed",
          }}
        >
          Exportar Excel
        </button>
      </div>

      {error && (
        <div style={{ background: "#fde8e8", color: "#c53030", padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: "#718096" }}>Cargando...</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <KpiCard label="Saldo Total por Cobrar" value={fmtMoney(saldoTotal)} />
            <KpiCard label={BUCKET_LABEL.verde} value={fmtMoney(totales.verde)} color={BUCKET_COLOR.verde} />
            <KpiCard label={BUCKET_LABEL.ambar} value={fmtMoney(totales.ambar)} color={BUCKET_COLOR.ambar} />
            <KpiCard label={BUCKET_LABEL.rojo} value={fmtMoney(totales.rojo)} color={BUCKET_COLOR.rojo} />
          </div>

          <div style={{ display: "flex", gap: 20, marginBottom: 24, flexWrap: "wrap" }}>
            <div style={{ background: "#fff", border: "1px solid #edf2f7", borderRadius: 10, padding: 20, flex: 1, minWidth: 320 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Antigüedad de Saldos</div>
              <AgingDonut totales={totales} />
            </div>

            {tab === "pendientes" && (
              <div style={{ background: "#fff", border: "1px solid #edf2f7", borderRadius: 10, padding: 20, flex: 1, minWidth: 280 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Resumen de Cobranza</div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
                  <label style={{ fontSize: 12 }}>
                    Desde{" "}
                    <input type="date" value={desdeResumen} onChange={(e) => setDesdeResumen(e.target.value)} />
                  </label>
                  <label style={{ fontSize: 12 }}>
                    Hasta{" "}
                    <input type="date" value={hastaResumen} onChange={(e) => setHastaResumen(e.target.value)} />
                  </label>
                </div>
                {errorResumen && (
                  <div style={{ background: "#fde8e8", color: "#c53030", padding: 8, borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
                    {errorResumen}
                  </div>
                )}
                {loadingResumen ? (
                  <div style={{ color: "#718096", fontSize: 13 }}>Cargando...</div>
                ) : resumen ? (
                  <div style={{ display: "flex", gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 12, color: "#718096" }}>Facturado</div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtMoney(resumen.facturado)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#718096" }}>Cobrado</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#2f855a" }}>{fmtMoney(resumen.cobrado)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#718096" }}>% de Cobranza</div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{resumen.pctCobranza}%</div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div style={{ background: "#fff", border: "1px solid #edf2f7", borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", textAlign: "left", color: "#718096" }}>
                  <th style={{ padding: "10px 12px" }}>Cliente</th>
                  <th style={{ padding: "10px 12px" }}>RFC</th>
                  <th style={{ padding: "10px 12px" }}>Facturas</th>
                  <th style={{ padding: "10px 12px" }}>Mayor atraso</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>Adeudo</th>
                </tr>
              </thead>
              <tbody>
                {clientesFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#a0aec0" }}>
                      Sin saldos pendientes para estos filtros.
                    </td>
                  </tr>
                ) : (
                  clientesFiltrados.map((cli) => <ClienteRow key={cli.rfc || cli.nombre} cli={cli} />)
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
    </div>
  );
}
